// src/components/StockCheckButton.tsx
'use client';
import { useEffect, useState, forwardRef } from 'react';
import StoreInfoModal from './StoreInfoModal';
import StoreInfoModalMG from './StoreInfoModalMG';
import StoreInfoModalNovza from './StoreInfoModalNovza';
import StoreInfoModalAviator from './StoreInfoModalAviator';
import StoreInfoModalNomo from './StoreInfoModalNomo';

interface StockCheckProps {
    sku: string;
    productName: string;
    productImage: string;
    onStockLoad?: (hasStock: boolean) => void;
}

interface ProductType {
    id: string;
    name: string;
    vendorCode: string;
    counts: {
        active?: number;
        writtenOff?: number;
    };
    officeName: string;
}

const STORE_NAMES = {
    FIDOKOR: 'Vitaline Store 1',
    MAXIM_GORKY: 'MG',
    NOVZA: 'Vitaline Novza',
    AVIATOR: 'Vitaline aviator',
    NOMO: 'NOMO VITALINE'
};

const STORE_DISPLAY_NAMES = {
    [STORE_NAMES.FIDOKOR]: 'филиал Фидокор',
    [STORE_NAMES.MAXIM_GORKY]: 'филиал Максим Горький',
    [STORE_NAMES.NOVZA]: 'филиал Новза',
    [STORE_NAMES.AVIATOR]: 'Korzinka Aviator',
    [STORE_NAMES.NOMO]: 'филиал Новомосковский'
};

const StockCheckButton = forwardRef<HTMLDivElement, StockCheckProps>(
    ({ sku, productName, productImage, onStockLoad }, ref) => {
        const [stockByStore, setStockByStore] = useState<Record<string, number>>({});
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [selectedStore, setSelectedStore] = useState<string>('');

        useEffect(() => {
            const checkStock = async () => {
                try {
                    // Теперь вызываем свой API route
                    const response = await fetch('/api/stock', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ sku }),
                    });

                    const responseData = await response.json();

                    if (!response.ok || responseData.error) {
                        throw new Error(responseData.error || 'Ошибка API');
                    }

                    if (!responseData.result?.data?.results || responseData.result.data.results.length === 0) {
                        throw new Error('Товар не найден');
                    }

                    const newStockByStore: Record<string, number> = Object.values(STORE_NAMES).reduce(
                        (acc, storeName) => ({ ...acc, [storeName]: 0 }),
                        {}
                    );

                    responseData.result.data.results.forEach((product: ProductType) => {
                        if (Object.values(STORE_NAMES).includes(product.officeName)) {
                            let stockValue = 0;
                            if (product.counts.active !== undefined) {
                                stockValue = product.counts.active;
                            }
                            newStockByStore[product.officeName] = stockValue;
                        }
                    });

                    setStockByStore(newStockByStore);

                    const hasAnyStock = Object.values(newStockByStore).some(stock => stock > 0);
                    onStockLoad?.(hasAnyStock);

                } catch (err) {
                    setError(err instanceof Error ? err.message : 'Ошибка при проверке остатков');
                    onStockLoad?.(false);
                } finally {
                    setLoading(false);
                }
            };

            checkStock();
        }, [sku, onStockLoad]);

        const openModal = (storeKey: string) => {
            setSelectedStore(storeKey);
            setIsModalOpen(true);
        };

        const closeModal = () => {
            setIsModalOpen(false);
        };

        return (
            <div ref={ref}>
                {/* Остальной код без изменений */}
                <div className="value_block mb-0">
                    <span className='flex items-center gap-2.5'>
                        Наличие в <span
                            onClick={() => openModal(STORE_NAMES.FIDOKOR)}
                            style={{ color: "#ff7900", textDecoration: "underline", cursor: "pointer" }}
                            className="hover:text-orange-600 transition-colors"
                        >
                            {STORE_DISPLAY_NAMES[STORE_NAMES.FIDOKOR]}:
                        </span> {' '}
                        {loading ? (
                            <span className="inline-block mt-[8px]">
                                <span className="animate-pulse bg-gray-200 h-5 w-16 rounded inline-block"></span>
                            </span>
                        ) : error ? (
                            <span className="text-red-500">ошибка, сервис временно недоступен</span>
                        ) : (
                            <span className="font-bold text-[#000]">{stockByStore[STORE_NAMES.FIDOKOR] || 0} шт.</span>
                        )}
                    </span>
                </div>

                <div className="value_block mb-0">
                    <span className='flex items-center gap-2.5'>
                        Наличие в <span
                            onClick={() => openModal(STORE_NAMES.MAXIM_GORKY)}
                            style={{ color: "#ff7900", textDecoration: "underline", cursor: "pointer" }}
                            className="hover:text-orange-600 transition-colors"
                        >
                            {STORE_DISPLAY_NAMES[STORE_NAMES.MAXIM_GORKY]}:
                        </span> {' '}
                        {loading ? (
                            <span className="inline-block mt-[8px]">
                                <span className="animate-pulse bg-gray-200 h-5 w-16 rounded inline-block"></span>
                            </span>
                        ) : error ? (
                            <span className="text-red-500">ошибка, сервис временно недоступен</span>
                        ) : (
                            <span className="font-bold text-[#000]">{stockByStore[STORE_NAMES.MAXIM_GORKY] || 0} шт.</span>
                        )}
                    </span>
                </div>

                <div className="value_block mb-0">
                    <span className='flex items-center gap-2.5'>
                        Наличие в <span
                            onClick={() => openModal(STORE_NAMES.NOVZA)}
                            style={{ color: "#ff7900", textDecoration: "underline", cursor: "pointer" }}
                            className="hover:text-orange-600 transition-colors"
                        >
                            {STORE_DISPLAY_NAMES[STORE_NAMES.NOVZA]}:
                        </span> {' '}
                        {loading ? (
                            <span className="inline-block mt-[8px]">
                                <span className="animate-pulse bg-gray-200 h-5 w-16 rounded inline-block"></span>
                            </span>
                        ) : error ? (
                            <span className="text-red-500">ошибка, сервис временно недоступен</span>
                        ) : (
                            <span className="font-bold text-[#000]">{stockByStore[STORE_NAMES.NOVZA] || 0} шт.</span>
                        )}
                    </span>
                </div>

                <div className="value_block mb-0">
                    <span className='flex items-center gap-2.5'>
                        Наличие в <span
                            onClick={() => openModal(STORE_NAMES.NOMO)}
                            style={{ color: "#ff7900", textDecoration: "underline", cursor: "pointer" }}
                            className="hover:text-orange-600 transition-colors"
                        >
                            {STORE_DISPLAY_NAMES[STORE_NAMES.NOMO]}:
                        </span> {' '}
                        {loading ? (
                            <span className="inline-block mt-[8px]">
                                <span className="animate-pulse bg-gray-200 h-5 w-16 rounded inline-block"></span>
                            </span>
                        ) : error ? (
                            <span className="text-red-500">ошибка, сервис временно недоступен</span>
                        ) : (
                            <span className="font-bold text-[#000]">{stockByStore[STORE_NAMES.NOMO] || 0} шт.</span>
                        )}
                    </span>
                </div>

                {/* <div className="value_block mb-5">
                    <span className='flex items-center gap-2.5'>
                        Наличие в <span
                            onClick={() => openModal(STORE_NAMES.AVIATOR)}
                            style={{ color: "#ff7900", textDecoration: "underline", cursor: "pointer" }}
                            className="hover:text-orange-600 transition-colors"
                        >
                            {STORE_DISPLAY_NAMES[STORE_NAMES.AVIATOR]}:
                        </span> {' '}
                        {loading ? (
                            <span className="inline-block mt-[8px]">
                                <span className="animate-pulse bg-gray-200 h-5 w-16 rounded inline-block"></span>
                            </span>
                        ) : error ? (
                            <span className="text-red-500">ошибка, сервис временно недоступен</span>
                        ) : (
                            <span className="font-bold text-[#000]">{stockByStore[STORE_NAMES.AVIATOR] || 0} шт.</span>
                        )}
                    </span>
                </div> */}

                {/* {selectedStore === STORE_NAMES.FIDOKOR ? (
                    <StoreInfoModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        storeName={STORE_DISPLAY_NAMES[STORE_NAMES.FIDOKOR]}
                    />
                ) : selectedStore === STORE_NAMES.MAXIM_GORKY ? (
                    <StoreInfoModalMG
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        storeName={STORE_DISPLAY_NAMES[STORE_NAMES.MAXIM_GORKY]}
                    />
                ) : selectedStore === STORE_NAMES.NOVZA ? (
                    <StoreInfoModalNovza
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        storeName={STORE_DISPLAY_NAMES[STORE_NAMES.NOVZA]}
                    />
                ) : (
                    <StoreInfoModalAviator
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        storeName={STORE_DISPLAY_NAMES[STORE_NAMES.AVIATOR]}
                    />
                )} */}

                {selectedStore === STORE_NAMES.FIDOKOR ? (
                    <StoreInfoModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        storeName={STORE_DISPLAY_NAMES[STORE_NAMES.FIDOKOR]}
                    />
                ) : selectedStore === STORE_NAMES.MAXIM_GORKY ? (
                    <StoreInfoModalMG
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        storeName={STORE_DISPLAY_NAMES[STORE_NAMES.MAXIM_GORKY]}
                    />
                ) : selectedStore === STORE_NAMES.NOMO ? (
                    <StoreInfoModalNomo
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        storeName={STORE_DISPLAY_NAMES[STORE_NAMES.NOMO]}
                    />
                ) : (
                    <StoreInfoModalNovza
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        storeName={STORE_DISPLAY_NAMES[STORE_NAMES.NOVZA]}
                    />
                )}
            </div>
        );
    }
);

StockCheckButton.displayName = 'StockCheckButton';

export default StockCheckButton;